import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../features/userSlice";
import {
  fetchAccessOverview,
  fetchAccessUsers,
} from "../../../views/Admin/Access/accessApi";
import { isOnline, timeAgo } from "../../../views/Admin/Access/accessUi";
import WidgetFrame, { WidgetEmpty, WidgetStat } from "./WidgetFrame";

// User Manager at a glance (Admin): active accounts, who's on right now,
// and accounts with no roles yet.
const UserManagerWidget = () => {
  const user = useSelector(selectUser);
  const [users, setUsers] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    fetchAccessUsers(user.id)
      .then((res) => setUsers(res.users || []))
      .catch(() => setFailed(true));
  }, [user?.id]);

  return (
    <WidgetFrame title="User Manager" to="/admin/user/userManager">
      {!users ? (
        <WidgetEmpty>{failed ? "Couldn't load users." : "Loading users…"}</WidgetEmpty>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          <WidgetStat value={users.filter((u) => !u.deactivated).length} label="Active" />
          <WidgetStat value={users.filter((u) => !u.deactivated && isOnline(u)).length} label="Online now" />
          <WidgetStat
            value={users.filter((u) => !u.deactivated && !(u.roles || []).length).length}
            label="No roles"
          />
        </div>
      )}
    </WidgetFrame>
  );
};

// Page Access at a glance (Admin): pages with extra access, custom roles,
// and the latest change.
export const PageAccessWidget = () => {
  const user = useSelector(selectUser);
  const [overview, setOverview] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    fetchAccessOverview(user.id)
      .then(setOverview)
      .catch(() => setFailed(true));
  }, [user?.id]);

  if (!overview) {
    return (
      <WidgetFrame title="Page Access" to="/admin/access">
        <WidgetEmpty>{failed ? "Couldn't load page access." : "Loading page access…"}</WidgetEmpty>
      </WidgetFrame>
    );
  }

  const last = overview.audit?.[0];
  const people = new Set(overview.grants.flatMap((g) => g.users)).size;

  return (
    <WidgetFrame title="Page Access" to="/admin/access">
      <div className="grid grid-cols-3 gap-2">
        <WidgetStat value={overview.grants.length} label="Pages opened up" />
        <WidgetStat value={people} label="People added" />
        <WidgetStat value={overview.customRoles.length} label="Custom roles" />
      </div>
      {last && (
        <p className="app-muted mt-2 truncate text-xs" title={last.summary}>
          {timeAgo(last.at)}: {last.summary}
        </p>
      )}
    </WidgetFrame>
  );
};

export default UserManagerWidget;
